import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { getAllPdvs, getTefCobrancasGestao } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { toDateInputValue } from '../utils/datetimeLocal';
import {
  escapeHtml as html,
  loadReportCompany,
  reportBrandHtml,
  REPORT_BRAND_CSS,
} from '../utils/reportBranding';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';

const STATUS_OPCOES = [
  { value: '', label: 'Todos' },
  { value: 'Pendente', label: 'Pendente' },
  { value: 'EmAndamento', label: 'Em andamento' },
  { value: 'Paga', label: 'Paga' },
  { value: 'Cancelada', label: 'Cancelada' },
  { value: 'Expirada', label: 'Expirada' },
  { value: 'Erro', label: 'Erro' },
];

function statusColor(status) {
  switch (status) {
    case 'Paga': return 'success';
    case 'EmAndamento': return 'info';
    case 'Pendente': return 'warning';
    case 'Erro': return 'error';
    case 'Cancelada':
    case 'Expirada':
      return 'default';
    default: return 'default';
  }
}

function formatarData(valor) {
  if (!valor) return '—';
  try {
    return new Date(valor).toLocaleString('pt-BR');
  } catch {
    return valor;
  }
}

function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const localDate = toDateInputValue;

export default function TefCobrancaList() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const [pdvs, setPdvs] = useState([]);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [valorTotalPago, setValorTotalPago] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [tamanho] = useState(25);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const [filtroDe, setFiltroDe] = useState(localDate(first));
  const [filtroAte, setFiltroAte] = useState(localDate(now));
  const [filtroCaixa, setFiltroCaixa] = useState('');
  const [filtroPos, setFiltroPos] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const caixas = useMemo(
    () => pdvs.filter((p) => !p.zenPay),
    [pdvs],
  );
  const terminais = useMemo(
    () => pdvs.filter((p) => p.zenPay),
    [pdvs],
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAllPdvs();
        setPdvs(Array.isArray(data) ? data : []);
      } catch {
        setPdvs([]);
      }
    })();
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params = {
        pagina,
        tamanho,
        de: filtroDe,
        ate: filtroAte,
      };
      if (filtroCaixa) params.pdvCaixaId = Number(filtroCaixa);
      if (filtroPos) params.pdvPosId = Number(filtroPos);
      if (filtroStatus) params.status = filtroStatus;

      const { data } = await getTefCobrancasGestao(params);
      setItens(data?.itens || []);
      setTotal(data?.total || 0);
      setValorTotalPago(data?.valorTotalPago || 0);
    } catch (err) {
      setErro(getApiErrorMessage(err, 'Não foi possível carregar as cobranças TEF.'));
      setItens([]);
      setTotal(0);
      setValorTotalPago(0);
    } finally {
      setCarregando(false);
    }
  }, [pagina, tamanho, filtroDe, filtroAte, filtroCaixa, filtroPos, filtroStatus]);
  useEffect(() => {
    carregar();
  }, [carregar]);

  const tefAccessors = useMemo(() => ({
    data: (c) => c.dataCriacao,
    caixa: (c) => c.pdvCaixaDescricao || c.pdvCaixaId,
    terminal: (c) => c.pdvPosDescricao || c.pdvPosId,
    valor: (c) => Number(c.valor) || 0,
    bandeira: (c) => c.bandeira || c.atk || '',
  }), []);
  const { sorted: itensOrdenados, orderBy, order, requestSort } = useClientSort(
    itens,
    'dataCriacao',
    'desc',
    tefAccessors,
  );

  const totalPaginas = Math.max(1, Math.ceil(total / tamanho));

  const exportPdf = async () => {
    const popup = window.open('', '_blank', 'width=1100,height=760');
    if (!popup) {
      setErro('Libere pop-ups para gerar o PDF.');
      return;
    }
    try {
      const company = await loadReportCompany();
      const rows = itens.map((c) => `<tr>
        <td>#${html(c.id)}</td>
        <td>${html(formatarData(c.dataCriacao))}</td>
        <td>${html(c.pdvCaixaDescricao || c.pdvCaixaId)}</td>
        <td>${html(c.pdvPosDescricao || c.pdvPosId)}</td>
        <td>${html(c.tipo)}</td>
        <td style="text-align:right">${html(formatarMoeda(c.valor))}</td>
        <td>${html(c.status)}</td>
        <td>${html(c.bandeira || '—')}</td>
      </tr>`).join('');
      popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>ZenPay</title>
        <style>
          body{font:11px Arial;padding:18px;color:#172033}
          ${REPORT_BRAND_CSS}
          table{width:100%;border-collapse:collapse;margin-top:12px}
          th,td{border-bottom:1px solid #e2e6ed;padding:6px;text-align:left}
          th{background:#eaf6fe}
        </style></head><body>
        ${reportBrandHtml(company, { title: 'Transações ZenPay / TEF' })}
        <div style="margin-top:8px">Período: ${html(filtroDe)} a ${html(filtroAte)}</div>
        <div>${html(total)} transação(ões) · Pagas: ${html(formatarMoeda(valorTotalPago))}</div>
        <table><thead><tr><th>ID</th><th>Data</th><th>Caixa</th><th>Terminal</th><th>Tipo</th><th>Valor</th><th>Status</th><th>Bandeira</th></tr></thead>
        <tbody>${rows}</tbody></table></body></html>`);
      popup.document.close();
      setTimeout(() => popup.print(), 300);
    } catch {
      popup.close();
      setErro('Falha ao gerar PDF.');
    }
  };

  return (    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <PageHeader
        title="Transações ZenPay"
        subtitle="Auditoria das transações remotas entre caixa e terminal Stone"
      />

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filtroDe}
            onChange={(e) => { setPagina(1); setFiltroDe(e.target.value); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filtroAte}
            onChange={(e) => { setPagina(1); setFiltroAte(e.target.value); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Caixa"            select
            value={filtroCaixa}
            onChange={(e) => { setPagina(1); setFiltroCaixa(e.target.value); }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {caixas.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.descricao}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Terminal ZenPay"
            select
            value={filtroPos}
            onChange={(e) => { setPagina(1); setFiltroPos(e.target.value); }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {terminais.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.descricao}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Status"
            select
            value={filtroStatus}
            onChange={(e) => { setPagina(1); setFiltroStatus(e.target.value); }}
            sx={{ minWidth: 180 }}
          >
            {STATUS_OPCOES.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={exportPdf}>
            Exportar PDF
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {total} transação(ões) · Total pago no período: {formatarMoeda(valorTotalPago)}
        </Typography>
      </Paper>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableTh id="id" label="ID" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="data" label="Data" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="caixa" label="Caixa" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="terminal" label="Terminal" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="tipo" label="Tipo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="valor" label="Valor" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="bandeira" label="Bandeira / ATK" orderBy={orderBy} order={order} onSort={requestSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : itens.length > 0 ? (
              itensOrdenados.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell data-label="ID">#{c.id}</TableCell>
                  <TableCell data-label="Data">{formatarData(c.dataCriacao)}</TableCell>
                  <TableCell data-label="Caixa">
                    {c.pdvCaixaDescricao || `#${c.pdvCaixaId}`}
                  </TableCell>
                  <TableCell data-label="Terminal">
                    {c.pdvPosDescricao || `#${c.pdvPosId}`}
                  </TableCell>
                  <TableCell data-label="Tipo">{c.tipo}</TableCell>
                  <TableCell data-label="Valor" align="right">{formatarMoeda(c.valor)}</TableCell>
                  <TableCell data-label="Status">
                    <Chip size="small" color={statusColor(c.status)} label={c.status} />
                  </TableCell>
                  <TableCell data-label="Bandeira">
                    {c.bandeira || '—'}
                    {c.atk ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        ATK {c.atk}
                      </Typography>
                    ) : null}
                    {c.mensagemErro ? (
                      <Typography variant="caption" display="block" color="error">
                        {c.mensagemErro}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma cobrança encontrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={(_e, p) => setPagina(p)}
          color="primary"
        />
        <Typography variant="body2" color="text.secondary">
          {total} cobrança(s) no total
        </Typography>
      </Stack>
    </Box>
  );
}
